import Messages from './messages.js'

export default class VideoClient {

  constructor(socket) {
    this.socket = socket
  }

  /**
   * Downloads a video and returns a handle to manage it.
   * @param {Object} options - Download options.
   * @param {string} options.videoId - The ID of the video.
   * @param {string} options.url - The URL to download the video from.
   * @param {Object} options.headers - Optional headers for the download request.
   * @param {string} options.checksum - Expected checksum for validation.
   * @param {Function} options.onStatus - Callback for status updates.
   * @returns {Object} - Download handle with a cancel method.
   */
  async download({ videoId, url, headers = {}, checksum, onStatus }) {
    console.debug(`Starting download for video with ID: ${videoId}`);
    try {
      await this.socket.emitWithAck(Messages.Video_Download, videoId, url, headers, checksum);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }

    const finalStatus = ['DONE', 'ERROR', 'CANCELLED', 'SERVER_ERROR'];

    const onStatusHandler = (statusUpdate) => {
      const payload = statusUpdate.data;
      onStatus(payload);
      if (finalStatus.includes(payload.status)) {
        console.debug(`Download in final status. id=${videoId}, status=${payload.status}, so cancelling the listener`);
        this.socket.off(Messages.Video_DownloadStatus, onStatusHandler);
      }
    };

    this.socket.on(Messages.Video_DownloadStatus, onStatusHandler);
  }

  /**
   * Cancel current video downloaing
   * @returns {Promise<void>}
   */
  async cancel(videoId) {
    console.debug(`Cancelling download for video with ID: ${videoId}`);
    try {
      await this.socket.emitWithAck(Messages.Video_DownloadCancel, videoId);
      console.debug(`Cancelled download for video with ID: ${videoId}`);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }
  }

  /**
   * Deletes a video by ID.
   * @param {string} videoId - The ID of the video to delete.
   * @returns {Promise<void>}
   */
  async delete(videoId) {
    console.debug(`Deleting video with ID: ${videoId}`);
    try {
      await this.socket.emitWithAck(Messages.Video_Delete, videoId);
      console.debug(`Deleted video with ID: ${videoId}`);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }
  }

  /**
   * Deletes all videos.
   * @returns {Promise<void>}
   */
  async deleteAll() {
    console.debug('Deleting all videos');
    try {
      await this.socket.emitWithAck(Messages.Video_DeleteAll);
      console.debug(`Deleted all videos`);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }
  }

  /**
   * Opens a preview for a video.
   * @param {string} videoId - The ID of the video to preview.
   * @param {string} previewKey - The key used for previewing.
   * @returns {Promise<void>}
   */
  async previewOpen(videoId, previewKey) {
    console.debug(`Opening preview for video ID: ${videoId}`);
    try {
      const response = await this.socket.emitWithAck(Messages.Video_PreviewOpen, videoId, previewKey);
      if (!response.success) {
        const error = `${response.error.code}:${response.error.message}`
        throw new Error(error);
      }
      console.debug(`Preview opened for video ID: ${videoId}`);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }
  }

  /**
   * Closes the preview for a video.
   * @param {string} videoId - The ID of the video to close preview for.
   * @returns {Promise<void>}
   */
  async previewClose(videoId) {
    console.debug(`Closing preview for video ID: ${videoId}`);
    try {
      const response = await this.socket.emitWithAck(Messages.Video_PreviewClose, videoId);
      if (!response.success) {
        const error = `${response.error.code}:${response.error.message}`
        throw new Error(error);
      }
      console.debug(`Preview closed for video ID: ${videoId}`);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }
  }

  /**
   * Opens a play session for a video.
   * @param {string} videoId - The ID of the video to play.
   * @param {string} playKey - The key used for playing the video.
   * @param {string} passcode - A passcode for access control.
   * @returns {Promise<void>}
   */
  async playOpen(videoId, playKey, passcode) {
    console.debug(`Opening play session for video ID: ${videoId}`);
    try {
      const response = await this.socket.emitWithAck(Messages.Video_PlayOpen, videoId, playKey, passcode);
      if (!response.success) {
        const error = `${response.error.code}:${response.error.message}`
        throw new Error(error);
      }
      console.debug(`Play opened for video ID: ${videoId}`);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }
  }

  /**
   * Closes the play session for a video.
   * @param {string} videoId - The ID of the video to close play for.
   * @returns {Promise<void>}
   */
  async playClose(videoId) {
    console.debug(`Closing play session for video ID: ${videoId}`);
    try {
      const response = await this.socket.emitWithAck(Messages.Video_PlayClose, videoId);
      if (!response.success) {
        const error = `${response.error.code}:${response.error.message}`
        throw new Error(error);
      }
      console.debug(`Play closed for video ID: ${videoId}`);
    } catch (error) {
      console.error(`VideoClient : ${error}`)
      throw new Error('Socket Error')
    }
  }
}
